import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { deleteItemImage } from 'helpers/itemPhotos';
import type { ItemPhotoRow } from 'helpers/itemPhotos';
import { logException } from 'helpers/loggers';
import {
  useHandleErrorNotification,
  useHandleSuccessNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';
import { useTranslation } from 'i18n';
import type { CropState } from './PhotoDrawer/EditPhotoContent';
import { uploadCroppedPhoto } from './PhotoDrawer/uploadCroppedPhoto';

export const usePhotoUpload = ({
  companyId,
  merchantId,
  menuUuid,
  onItemPhotoUpdated,
  onPhotoChanged,
}: {
  companyId: string | number | undefined;
  merchantId: string | number | undefined;
  menuUuid?: string;
  onItemPhotoUpdated?: (item: ItemPhotoRow) => void;
  onPhotoChanged?: () => void;
}) => {
  const { t } = useTranslation('menuPhotos');
  const [selectedItem, setSelectedItem] = useState<ItemPhotoRow | null>(null);
  const [photoDrawerOpened, { open, close: closePhotoDrawer }] = useDisclosure();
  const handleErrorNotification = useHandleErrorNotification();
  const handleSuccessNotification = useHandleSuccessNotification();

  const openPhotoDrawer = (item: ItemPhotoRow) => {
    setSelectedItem(item);
    open();
  };

  // No-op: PhotoDrawer captures the blob URL internally via handleUploadWithCapture.
  // The actual Cuboh upload happens at save time with the cropped result.
  const handleUpload = (): Promise<void> => Promise.resolve();

  const handleDelete = async (item: ItemPhotoRow) => {
    if (!companyId || !merchantId) {
      logException({
        message: 'Cannot delete item photo: missing companyId or merchantId',
        error: null,
        context: { menuUuid, itemUuid: item.uuid },
        tags: null,
      });
      return;
    }

    try {
      const { item: refreshed } = await deleteItemImage({
        companyId,
        merchantId,
        itemUuid: item.uuid,
      });
      onItemPhotoUpdated?.(refreshed);
      onPhotoChanged?.();
      handleSuccessNotification({ customMessage: t('MenuPhotos.deletePhotoSuccess') });
    } catch (error) {
      logException({
        message: 'Failed to delete item photo',
        error,
        context: { menuUuid, itemUuid: item.uuid },
        tags: null,
      });
      handleErrorNotification({ customMessage: t('MenuPhotos.deletePhotoError') });
    }
  };

  const handleSavePhoto = async (
    cropState: CropState,
    imageUrl: string,
    enhancedAt?: Date | null,
  ): Promise<void> => {
    if (!selectedItem) return;
    if (!companyId || !merchantId) {
      // Throw so the drawer surfaces the failure instead of silently showing success.
      logException({
        message: 'Cannot save item photo: missing companyId or merchantId',
        error: null,
        context: { menuUuid, itemUuid: selectedItem.uuid },
        tags: null,
      });
      throw new Error(t('MenuPhotos.savePhotoError'));
    }
    const { item: refreshed } = await uploadCroppedPhoto(imageUrl, cropState, {
      companyId,
      enhancedAt: enhancedAt?.toISOString(),
      merchantId,
      itemUuid: selectedItem.uuid,
    });
    onItemPhotoUpdated?.(refreshed);
    onPhotoChanged?.();
  };

  return {
    selectedItem,
    photoDrawerOpened,
    openPhotoDrawer,
    closePhotoDrawer,
    handleUpload,
    handleDelete,
    handleSavePhoto,
  };
};
